import { useState, useEffect } from "react";
import { useSearchParams } from "react-router-dom";
import { useQuery } from "@tanstack/react-query";
import { publicApi } from "@/lib/api";
import { useContent } from "@/lib/content";
import ProductCard from "@/components/ProductCard";
import { Search } from "lucide-react";

export default function Catalog() {
  const [params, setParams] = useSearchParams();
  const activeCat = params.get("category") || "";
  const [search, setSearch] = useState("");
  const [debounced, setDebounced] = useState("");
  const { t } = useContent();

  useEffect(() => {
    const t = setTimeout(() => setDebounced(search), 300);
    return () => clearTimeout(t);
  }, [search]);

  const { data: categories = [] } = useQuery({ queryKey: ["categories"], queryFn: async () => (await publicApi.get("/categories")).data });
  const { data: products = [], isLoading } = useQuery({
    queryKey: ["catalog", activeCat, debounced],
    queryFn: async () => (await publicApi.get("/products", { params: { category: activeCat || undefined, search: debounced || undefined } })).data,
  });

  const setCategory = (slug) => {
    const next = new URLSearchParams(params);
    if (slug) next.set("category", slug); else next.delete("category");
    setParams(next);
  };

  return (
    <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-12">
      <div className="mb-8">
        <p className="overline-label mb-2">{t("catalog.overline")}</p>
        <h1 className="font-serif text-4xl lg:text-5xl text-cream">{t("catalog.title")}</h1>
      </div>

      <div className="relative mb-6 max-w-md">
        <Search className="absolute left-4 top-1/2 -translate-y-1/2 w-4 h-4 text-muted2" />
        <input
          data-testid="catalog-search-input"
          value={search}
          onChange={(e) => setSearch(e.target.value)}
          placeholder={t("catalog.search_placeholder")}
          className="w-full bg-ink-700 border border-gold/25 rounded-full pl-11 pr-4 py-3 text-cream focus:outline-none focus:border-gold transition-colors"
        />
      </div>

      <div className="flex flex-wrap gap-2 mb-10">
        <button data-testid="catalog-filter-all" onClick={() => setCategory("")}
          className={`px-4 py-2 rounded-full text-sm transition-colors ${!activeCat ? "bg-gold text-ink" : "bg-ink-700 text-cream/80 border border-gold/20 hover:border-gold"}`}>
          All
        </button>
        {categories.map((c) => (
          <button key={c.slug} data-testid={`catalog-filter-${c.slug}`} onClick={() => setCategory(c.slug)}
            className={`px-4 py-2 rounded-full text-sm transition-colors ${activeCat === c.slug ? "bg-gold text-ink" : "bg-ink-700 text-cream/80 border border-gold/20 hover:border-gold"}`}>
            {c.name}
          </button>
        ))}
      </div>

      {isLoading ? (
        <p className="text-muted2">Loading…</p>
      ) : products.length === 0 ? (
        <p data-testid="catalog-empty" className="text-muted2 py-20 text-center">{t("catalog.empty")}</p>
      ) : (
        <div data-testid="catalog-grid" className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 gap-6">
          {products.map((p) => <ProductCard key={p.id} product={p} />)}
        </div>
      )}
    </div>
  );
}
